import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import api from "@/lib/api";
import { routedThankYou } from "@/lib/routedBuyer";
import { TCPA_LANGUAGE, PI_CASE_TYPES, siteText } from "./config";
import {
  isValidEmail,
  isValidZip,
  INCIDENT_AGE_BUCKETS,
} from "./publicFormValidators";
import { formatUSPhone, isValidUSPhone } from "./publicFormPhone";
import {
  AFPI_CHOICES,
  requiresAtFaultPartyInsured,
} from "./mvaApplicability";
import { emitPreviewEngagement } from "@/lib/prospectEngagement";
import { fieldRulePage, useFieldRules, zipOk } from "@/lib/useFieldRules";

const OPT_STYLE = { fontWeight: 400, color: "#94a3b8" };
const Mark = ({ required }) => (required
  ? <span className="lp-req">*</span>
  : <span style={OPT_STYLE}> (optional)</span>);
import { ConsentText, readTrustedFormCert, trustedFormOn, useTrustedFormScript } from "./trustedForm";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","District of Columbia"],
];

const YesNo = ({ label, value, onChange, testIdPrefix, optional = false }) => (
  <div className="lp-field">
    <label>{label}<Mark required={!optional} /></label>
    <div className="lp-yesno">
      <button
        type="button"
        className={value === true ? "active" : ""}
        onClick={() => onChange(true)}
        data-testid={`${testIdPrefix}-yes`}
      >
        Yes
      </button>
      <button
        type="button"
        className={value === false ? "active" : ""}
        onClick={() => onChange(false)}
        data-testid={`${testIdPrefix}-no`}
      >
        No
      </button>
    </div>
  </div>
);

// Iter-63.1.12A — tri-state (Yes / No / I'm Not Sure) selector for the
// `at_fault_party_insured` question. Rendered only when the effective
// case type is applicable (see mvaApplicability.requiresAtFaultPartyInsured).
const TriState = ({ label, value, onChange, testIdPrefix, optional = false }) => (
  <div className="lp-field">
    <label>{label}<Mark required={!optional} /></label>
    <div className="lp-yesno">
      {AFPI_CHOICES.map((c) => (
        <button
          key={c.value}
          type="button"
          className={value === c.value ? "active" : ""}
          onClick={() => onChange(c.value)}
          data-testid={`${testIdPrefix}-${c.value}`}
        >
          {c.label}
        </button>
      ))}
    </div>
  </div>
);

export default function LandingForm({ vertical, preview = false, demo = null }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [aff, setAff] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Iter-33: in preview mode we show a fake "success" state instead of POSTing.
  // Iter-50: same treatment applies to `demo` (client-share preview token).
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const previewSurface = preview || Boolean(demo);
  const tfFormRef = useRef(null);
  useTrustedFormScript(tfFormRef, trustedFormOn({ preview, demo, vertical }));

  // Iter-46: anti-bot signals — Turnstile token + form-load timestamp.
  // The honeypot lives inside the form state (`hp_company`) below.
  const [turnstileToken, setTurnstileToken] = useState("");
  const { req } = useFieldRules({ style: "standard", page: fieldRulePage(vertical), buyer: vertical.buyerCode });
  const formLoadMs = useRef(Date.now());
  const turnstileRef = useRef(null);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY) && !previewSurface;

  const affRef = params.get("aff") || "";
  const subId = params.get("sub") || params.get("sub_id") || "";

  // Iter-56.1: emit `form_start` exactly once per landing-page session
  // on the first meaningful field interaction. NEVER carries field
  // names or field values — only the PII-safe context the parent has
  // already committed to sharing with sandboxed custom tracking. Guarded
  // against preview / demo / sample so we don't pollute third-party
  // trackers during ops walkthroughs.
  const formStartFiredRef = useRef(false);
  const emitFormStart = () => {
    if (formStartFiredRef.current) return;
    if (vertical?.isSamplePage) return;
    formStartFiredRef.current = true;
    if (!previewSurface) {
      try {
        window.dispatchEvent(new CustomEvent("lf-tracking-emit", {
          detail: { event: "form_start", context: {} },
        }));
      } catch (_e) { /* noop */ }
    }
    // Iter-63.1: prospect-share `form_start` — only fires on preview
    // surfaces carrying a share token, never on real live traffic and
    // never on sample pages.
    if (demo && vertical?.customSlug && !vertical?.isSamplePage) {
      emitPreviewEngagement(demo, vertical.customSlug, "form_start", {
        isSamplePage: vertical.isSamplePage,
      });
    }
  };

  useEffect(() => {
    if (!affRef) return;
    let cancelled = false;
    api
      .get(`/public/landing/aff/${encodeURIComponent(affRef)}`)
      .then((r) => !cancelled && setAff(r.data))
      .catch(() => !cancelled && setAff(null));
    return () => { cancelled = true; };
  }, [affRef]);

  const [form, setForm] = useState({
    // Step 1 — qualifying
    case_type: vertical.caseTypeSelector ? "" : null,
    // Iter-62.3.2: PRIMARY incident-timing question is now the shared
    // five-window `incident_age_bucket`. The exact-date `incident_date`
    // input is a SECONDARY, OPTIONAL follow-up — we never fabricate a
    // date from the bucket.
    incident_age_bucket: "",
    incident_date: "",
    physically_injured: null,
    medical_treatment: null,
    at_fault: null,
    currently_represented: null,
    // Iter-63.1.12A — canonical MVA structured field. Values: "yes" | "no"
    // | "unknown". Required only when the effective accident type belongs
    // to `mvaApplicability.CANONICAL_MVA_CASE_TYPES`.
    at_fault_party_insured: null,
    accident_description: "",
    // Iter-44: evidence (all optional)
    police_report_number: "",
    accident_photos: [],  // [{file_id, filename, previewUrl, uploading?}]
    // Step 2 — contact
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    state: "",
    zip: "",
    consent: false,
    // Iter-46: honeypot. Real users never fill this — it's visually hidden
    // via CSS + aria-hidden. Any non-empty value = bot.
    hp_company: "",
  });

  const update = (k, v) => {
    // Iter-56.1: any meaningful field change counts as "form_start".
    // We intentionally do NOT pass k or v to the tracking bridge — the
    // sandbox never sees field names or values.
    emitFormStart();
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Global funnels: report state / case type / ZIP so a neutral page can
  // name the matched firm in the consent text.
  useEffect(() => {
    vertical.onAnswers?.(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.state, form.case_type, form.zip, vertical.onAnswers]);

  // ------ Accident-photo upload (iter-44) ------
  const MAX_PHOTOS = 6;
  const MAX_PHOTO_MB = 8;
  const [photoErr, setPhotoErr] = useState("");

  const uploadPhotos = async (fileList) => {
    setPhotoErr("");
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const slotsLeft = MAX_PHOTOS - form.accident_photos.length;
    if (slotsLeft <= 0) {
      setPhotoErr(`Max ${MAX_PHOTOS} photos.`);
      return;
    }
    const toUpload = incoming.slice(0, slotsLeft);
    for (const file of toUpload) {
      if (!/^image\//.test(file.type)) {
        setPhotoErr(`${file.name} is not an image.`);
        continue;
      }
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        setPhotoErr(`${file.name} exceeds ${MAX_PHOTO_MB} MB.`);
        continue;
      }
      const placeholder = {
        clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        filename: file.name,
        previewUrl: URL.createObjectURL(file),
        uploading: true,
      };
      setForm((f) => ({ ...f, accident_photos: [...f.accident_photos, placeholder] }));
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/public/landing/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({
          ...f,
          accident_photos: f.accident_photos.map((p) =>
            p.clientId === placeholder.clientId
              ? { ...p, file_id: r.data.file_id, url: r.data.url, uploading: false }
              : p,
          ),
        }));
      } catch (err) {
        setPhotoErr(err.response?.data?.detail || `${file.name} failed to upload.`);
        setForm((f) => ({
          ...f,
          accident_photos: f.accident_photos.filter((p) => p.clientId !== placeholder.clientId),
        }));
      }
    }
  };

  const removePhoto = (clientId) => {
    setForm((f) => ({
      ...f,
      accident_photos: f.accident_photos.filter((p) => p.clientId !== clientId),
    }));
  };

  // Iter-63.1.12A — effective case type for AFPI applicability. On
  // all-in-one landers the visitor picks case_type from a dropdown; on
  // vertical-specific landers the accident type is derived from
  // `vertical.slug` (e.g. `auto-accident` → auto).
  const afpiApplicable = useMemo(() => requiresAtFaultPartyInsured({
    caseType: form.case_type || (vertical.accidentType && vertical.accidentType !== "other" ? vertical.accidentType : null),
    verticalSlug: vertical.slug,
  }), [form.case_type, vertical.slug, vertical.accidentType]);

  const step1Valid = useMemo(() => {
    const baseValid =
      // Iter-62.3.2: the five-window bucket is the PRIMARY qualifier;
      // the exact date input remains an OPTIONAL follow-up.
      (!req("incident_age_bucket") || !!form.incident_age_bucket) &&
      (!req("physically_injured") || form.physically_injured !== null) &&
      (!req("medical_treatment") || form.medical_treatment !== null) &&
      (!req("at_fault") || form.at_fault !== null) &&
      (!req("currently_represented") || form.currently_represented !== null) &&
      (!req("accident_description") || !!form.accident_description.trim()) &&
      (!req("police_report_number") || !!form.police_report_number.trim()) &&
      (!req("accident_photos") || form.accident_photos.some((p) => p.file_id));
    // Iter-63.1.12A -- AFPI must be answered when the case is an MVA.
    const afpiValid = afpiApplicable && req("at_fault_party_insured")
      ? typeof form.at_fault_party_insured === "string"
        && form.at_fault_party_insured.length > 0
      : true;
    // On the all-in-one landers case_type must be picked before Continue.
    if (vertical.caseTypeSelector) {
      return baseValid && !!form.case_type && afpiValid;
    }
    return baseValid && afpiValid;
  }, [form, vertical, afpiApplicable, req]);

  const step2Valid = useMemo(() => {
    return (
      !!form.first_name.trim() &&
      !!form.last_name.trim() &&
      // Iter-62.3.2: shared strict phone validator — 10 digits, or 11
      // starting with `1`. Formatting characters are stripped before
      // the length check so bad numbers cannot look valid.
      isValidUSPhone(form.phone) &&
      !!form.state &&
      // Iter-74.2 — Email + ZIP are REQUIRED on public landing pages
      // (reversing the Iter-63.1.12A opt-out). Both must parse.
      !!form.email.trim() && isValidEmail(form.email) &&
      zipOk(form.zip, req("zip"), isValidZip) &&
      form.consent === true
    );
  }, [form, req]);

  const submit = async (e) => {
    e.preventDefault();
    if (!step2Valid) {
      setError("Please complete all required fields and consent to continue.");
      return;
    }
    setError("");
    // Iter-33/50: preview OR client-share demo mode — never POST. Show a
    // fake success state so a Buyer can walk through the whole flow
    // without polluting the DB or firing ad-platform conversions.
    if (previewSurface) {
      // Iter-63.1: prospect-share `cta_primary_click` (submit button
      // press on a demo surface counts as intent).
      if (demo && vertical.customSlug && !vertical.isSamplePage) {
        emitPreviewEngagement(demo, vertical.customSlug, "cta_primary_click", {
          isSamplePage: vertical.isSamplePage,
        });
      }
      // Iter-50: for demo (buyer-share) links, forward them to a
      // demo Thank-You page so the buyer sees BOTH surfaces in one flow.
      if (demo && vertical.customSlug) {
        const q = new URLSearchParams({
          slug: vertical.customSlug,
          demo,
          ...(vertical.firmName ? { firm: vertical.firmName } : {}),
          ...(vertical.firmPhone ? { phone: vertical.firmPhone } : {}),
          ...(vertical.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
          ...(vertical.buyerCode ? { b: vertical.buyerCode } : {}),
        });
        nav(`/lp/thank-you?${q.toString()}`);
        return;
      }
      setPreviewSubmitted(true);
      return;
    }
    setSubmitting(true);
    // Iter-54.1: sample landing pages skip the TrustedForm cert read
    // entirely — the backend short-circuits to a DEMO reference anyway,
    // and we never want a demo view to claim a production cert.
    const isSample = Boolean(vertical.isSamplePage);
    const trustedform_url = isSample ? null : readTrustedFormCert();

    const payload = {
      aff: affRef || null,
      sub_id: subId || null,
      landing_page: vertical.slug,
      case_type: form.case_type || null,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      state: form.state,
      zip: form.zip.trim() || null,
      // Iter-62.3.2: exact date remains OPTIONAL. Never fabricated
      // from the bucket — see `publicFormValidators.INCIDENT_AGE_BUCKETS`.
      incident_date: form.incident_date || null,
      incident_age_bucket: form.incident_age_bucket || null,
      at_fault: form.at_fault,
      currently_represented: form.currently_represented,
      physically_injured: form.physically_injured,
      medical_treatment: form.medical_treatment,
      // Iter-63.1.12A — canonical MVA structured field.
      at_fault_party_insured: form.at_fault_party_insured || null,
      accident_description: form.accident_description.trim() || null,
      police_report_number: form.police_report_number.trim() || null,
      accident_photo_ids: form.accident_photos
        .filter((p) => p.file_id && !p.uploading)
        .map((p) => p.file_id),
      consent: form.consent,
      // Custom buyer pages pass their admin-authored TCPA text through so
      // the recorded consent language is firm-specific for compliance.
      tcpa_language: (vertical.tcpaLanguage && vertical.tcpaLanguage.trim())
        ? vertical.tcpaLanguage
        : TCPA_LANGUAGE,
      trustedform_url,
      landing_url: window.location.href,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      // Iter-46: anti-bot signals
      turnstile_token: turnstileToken || null,
      company: form.hp_company || null,  // honeypot
      load_ms: Date.now() - formLoadMs.current,
      buyer_code: vertical.buyerCode || null,
      buyer_source: vertical.buyerSource || null,
    };

    try {
      const { data } = await api.post("/public/landing/lead", payload);
      const q = new URLSearchParams({
        lead_id: data.id,
        vertical: vertical.slug,
        ...(data.is_test ? { test: "1" } : {}),
        ...routedThankYou(vertical, data),
        ...(vertical.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
      });
      nav(`/lp/thank-you?${q.toString()}`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong — please try again.";
      setError(typeof msg === "string" ? msg : "Submission failed. Please try again.");
      setSubmitting(false);
      // A Turnstile token works once: get a fresh one for the retry.
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="lp-form-card" data-testid="lp-form-card">
      {previewSubmitted && (
        <div className="lp-preview-success" data-testid="lp-preview-success">
          <div className="lp-preview-success-badge">✓</div>
          <div className="lp-preview-success-title">Nice — this is where a real lead would land.</div>
          <div className="lp-preview-success-sub">
            In production, this consumer&apos;s info + TCPA consent + TrustedForm
            cert would be POSTed to your intake pipeline. In preview it stops here.
          </div>
          <button
            type="button"
            onClick={() => { setPreviewSubmitted(false); setStep(1); }}
            data-testid="lp-preview-reset"
            className="lp-btn-secondary"
            style={{ marginTop: 12 }}
          >
            ↺ Try the flow again
          </button>
        </div>
      )}
      {aff && aff.is_sandbox && (
        <div className="lp-sandbox-banner" data-testid="lp-sandbox-banner">
          SANDBOX MODE — This submission is a TEST and will NOT be delivered to a buyer.
        </div>
      )}
      <div className="lp-form-header">
        <div className="lp-form-title" data-testid="lp-form-title">
          {vertical.firmName ? `${vertical.firmName} — Free Case Review` : "Free Case Review"}
        </div>
        <div className="lp-form-sub">
          {step === 1
            ? "Step 1 of 2 — Tell us about your case"
            : "Step 2 of 2 — How can our legal team reach you?"}
        </div>
        <div className="lp-progress" aria-label="form progress">
          <div className={`lp-progress-step ${step >= 1 ? "active" : ""}`} />
          <div className={`lp-progress-step ${step >= 2 ? "active" : ""}`} />
        </div>
      </div>

      {error && <div className="lp-error" data-testid="lp-form-error">{error}</div>}

      <form ref={tfFormRef} onSubmit={submit} noValidate data-tf-element-role="offer">
        {step === 1 && (
          <>
            {vertical.caseTypeSelector && (
              <div className="lp-field">
                <label htmlFor="lp-case-type">
                  What type of case is this?<span className="lp-req">*</span>
                </label>
                <select
                  id="lp-case-type"
                  className="lp-select"
                  value={form.case_type || ""}
                  onChange={(e) => update("case_type", e.target.value)}
                  data-testid="lp-case-type"
                >
                  <option value="">Select case type…</option>
                  {PI_CASE_TYPES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Iter-62.3.2: PRIMARY incident-timing question — one
                consistent five-window UX across Standard / Quiz /
                Focused Funnel. Do NOT fabricate a date from a bucket. */}
            {/* Iter-63.1.2: incident-timing question is a dropdown with
                plain-English labels. Same disjoint bucket VALUES so
                buyer routing / eligibility is unchanged; only the UX
                and copy are simpler. */}
            <div className="lp-field">
              <label htmlFor="lp-incident-age-bucket-select">
                When did the accident happen?<Mark required={req("incident_age_bucket")} />
              </label>
              <select
                id="lp-incident-age-bucket-select"
                className="lp-select"
                value={form.incident_age_bucket}
                onChange={(e) => update("incident_age_bucket", e.target.value)}
                data-testid="lp-incident-age-bucket"
              >
                <option value="">Select when the accident happened…</option>
                {INCIDENT_AGE_BUCKETS.map((b) => (
                  <option
                    key={b.value}
                    value={b.value}
                    data-testid={`lp-incident-age-bucket-${b.value}`}
                  >
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Iter-63.1.2: optional exact-date follow-up removed — the
                bucket dropdown alone is enough for buyer routing, and
                the redundant question added friction. */}
            <YesNo
              label="Were you physically injured?"
              value={form.physically_injured}
              onChange={(v) => update("physically_injured", v)}
              testIdPrefix="lp-injured"
              optional={!req("physically_injured")}
            />
            <YesNo
              label="Have you seen a doctor for your injuries?"
              value={form.medical_treatment}
              onChange={(v) => update("medical_treatment", v)}
              testIdPrefix="lp-treatment"
              optional={!req("medical_treatment")}
            />
            <YesNo
              label="Were you at fault for the incident?"
              value={form.at_fault}
              onChange={(v) => update("at_fault", v)}
              testIdPrefix="lp-atfault"
              optional={!req("at_fault")}
            />
            <YesNo
              label="Are you currently represented by an attorney?"
              value={form.currently_represented}
              onChange={(v) => update("currently_represented", v)}
              testIdPrefix="lp-represented"
              optional={!req("currently_represented")}
            />
            {/* Iter-63.1.12A — canonical structured MVA field. Shown only
                when the effective case type is an MVA per
                mvaApplicability.CANONICAL_MVA_CASE_TYPES. */}
            {afpiApplicable && (
              <TriState
                label="Was the other driver or at-fault party insured?"
                value={form.at_fault_party_insured}
                onChange={(v) => update("at_fault_party_insured", v)}
                testIdPrefix="lp-afpi"
                optional={!req("at_fault_party_insured")}
              />
            )}
            <div className="lp-field">
              <label htmlFor="lp-desc">
                {vertical.caseDescriptionLabel || "Briefly describe what happened"}
                <Mark required={req("accident_description")} />
              </label>
              <textarea
                id="lp-desc"
                className="lp-textarea"
                value={form.accident_description}
                onChange={(e) => update("accident_description", e.target.value)}
                placeholder="A few sentences helps our attorneys understand your case…"
                data-testid="lp-description"
                maxLength={1500}
              />
            </div>

            {/* Iter-44: Police report number (optional) */}
            <div className="lp-field">
              <label htmlFor="lp-police-report">
                Police report number
                <Mark required={req("police_report_number")} />
              </label>
              <input
                id="lp-police-report"
                className="lp-input"
                value={form.police_report_number}
                onChange={(e) => update("police_report_number", e.target.value)}
                placeholder="e.g. 2026-AL-0031982"
                data-testid="lp-police-report"
                maxLength={80}
              />
            </div>

            {/* Iter-44: Accident photos (optional, up to 6) */}
            <div className="lp-field">
              <label>
                Accident photos{" "}
                <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                  ({req("accident_photos") ? "required" : "optional"} · up to {MAX_PHOTOS})
                </span>
              </label>
              <div className="lp-photos-grid" data-testid="lp-photos-grid">
                {form.accident_photos.map((p) => (
                  <div key={p.clientId} className="lp-photo-tile" data-testid={`lp-photo-${p.clientId}`}>
                    <img
                      src={p.previewUrl}
                      alt={p.filename}
                      className="lp-photo-thumb"
                      loading="lazy"
                    />
                    {p.uploading && (
                      <div className="lp-photo-uploading">Uploading…</div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p.clientId)}
                      className="lp-photo-remove"
                      data-testid={`lp-photo-remove-${p.clientId}`}
                      aria-label={`Remove ${p.filename}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {form.accident_photos.length < MAX_PHOTOS && (
                  <label className="lp-photo-add" data-testid="lp-photo-add">
                    <span className="lp-photo-add-icon">+</span>
                    <span className="lp-photo-add-label">Add photos</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }}
                      data-testid="lp-photo-input"
                    />
                  </label>
                )}
              </div>
              {photoErr && (
                <div className="lp-photo-error" data-testid="lp-photo-error">{photoErr}</div>
              )}
            </div>

            <button
              type="button"
              className="lp-cta"
              disabled={!step1Valid}
              onClick={() => setStep(2)}
              data-testid="lp-next-btn"
            >
              Continue →
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="lp-row2">
              <div className="lp-field">
                <label htmlFor="lp-first">First name<span className="lp-req">*</span></label>
                <input
                  id="lp-first"
                  className="lp-input"
                  name="first_name"
                  autoComplete="given-name"
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  data-testid="lp-first-name"
                />
              </div>
              <div className="lp-field">
                <label htmlFor="lp-last">Last name<span className="lp-req">*</span></label>
                <input
                  id="lp-last"
                  className="lp-input"
                  name="last_name"
                  autoComplete="family-name"
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  data-testid="lp-last-name"
                />
              </div>
            </div>

            <div className="lp-field">
              <label htmlFor="lp-phone">Best phone number<span className="lp-req">*</span></label>
              <input
                id="lp-phone"
                className="lp-input"
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                placeholder="585-545-8888"
                value={form.phone}
                onChange={(e) => update("phone", formatUSPhone(e.target.value))}
                data-testid="lp-phone"
              />
            </div>
            <div className="lp-field">
              <label htmlFor="lp-email">
                Email<span className="lp-req">*</span>
              </label>
              <input
                id="lp-email"
                className="lp-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="lp-email"
              />
            </div>

            <div className="lp-row2">
              <div className="lp-field">
                <label htmlFor="lp-state">State where incident happened<span className="lp-req">*</span></label>
                <select
                  id="lp-state"
                  name="state"
                  autoComplete="address-level1"
                  className="lp-select"
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                  data-testid="lp-state"
                >
                  <option value="">Select state…</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="lp-field">
                <label htmlFor="lp-zip">
                  ZIP<Mark required={req("zip")} />
                </label>
                <input
                  id="lp-zip"
                  className="lp-input"
                  name="zip"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  maxLength={10}
                  required={req("zip")}
                  value={form.zip}
                  onChange={(e) => update("zip", e.target.value)}
                  data-testid="lp-zip"
                />
              </div>
            </div>

            <label className="lp-consent" htmlFor="lp-consent" data-tf-element-role="consent-language">
              <input
                id="lp-consent"
                name="consent"
                type="checkbox"
                checked={form.consent}
                onChange={(e) => update("consent", e.target.checked)}
                data-tf-element-role="consent-opt-in"
                data-testid="lp-consent"
              />
              <span className="lp-consent-text" data-testid="lp-consent-text">
                <ConsentText
                  text={siteText((vertical.tcpaLanguage && vertical.tcpaLanguage.trim()) ? vertical.tcpaLanguage : TCPA_LANGUAGE)}
                  advertiser={vertical.firmName}
                />
              </span>
            </label>

            {/* Iter-46: honeypot — visually hidden but a valid form field
                so bots that autofill every input trip it. Real humans
                never see or focus this. */}
            <div className="lp-honeypot" aria-hidden="true">
              <label htmlFor="lp-company-hp">Company (leave blank)</label>
              <input
                id="lp-company-hp"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.hp_company}
                onChange={(e) => update("hp_company", e.target.value)}
                data-testid="lp-honeypot"
              />
            </div>

            {/* Iter-46: invisible Cloudflare Turnstile. Absorbed silently
                unless a visitor triggers a challenge (very rare). */}
            {turnstileEnabled && (
              <div className="lp-turnstile" data-testid="lp-turnstile-wrap">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{
                    size: "flexible",
                    theme: "light",
                    action: "lp_lead_form",
                    appearance: "interaction-only",
                  }}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken("")}
                  onError={() => setTurnstileToken("")}
                />
              </div>
            )}

            <button
              type="submit"
              className="lp-cta"
              disabled={!step2Valid || submitting || (turnstileEnabled && !turnstileToken)}
              name="submit"
              data-tf-element-role="submit"
              data-testid="lp-submit-btn"
            >
              {submitting ? "Submitting…" : vertical.ctaText || "Get My Free Case Review"}
            </button>
            {error && <div className="lp-error" data-testid="lp-form-error-near-submit">{error}</div>}
            {turnstileEnabled && !turnstileToken && !submitting && (
              <div className="text-[12px] text-slate-500 mt-1" data-testid="lp-turnstile-hint">Complete the security check above to continue.</div>
            )}

            <div className="lp-btn-row">
              <button
                type="button"
                className="lp-btn-secondary"
                onClick={() => setStep(1)}
                data-testid="lp-back-btn"
              >
                ← Back
              </button>
            </div>

            <div className="lp-disclaimer">
              By clicking above I agree consent is <strong>not required</strong> to
              receive services and I may revoke it any time.
            </div>
          </>
        )}
      </form>
    </div>
  );
}
